import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Dumbbell, Calendar, LogOut, Shield, RefreshCw, Clock, MapPin, Key, Lock, User } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [classes, setClasses] = useState([]);
  const [rebookableClasses, setRebookableClasses] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [bookingsRes, classesRes] = await Promise.all([
        apiClient.get('/bookings'),
        apiClient.get('/classes')
      ]);
      setBookings(bookingsRes.data);
      setClasses(classesRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setDataLoading(false);
    }
  }, []);

  const fetchRebookableClasses = useCallback(async () => {
    try {
      const response = await apiClient.get('/bookings/history');
      setRebookableClasses(response.data);
    } catch (error) {
      console.error('Failed to fetch rebookable classes:', error.message);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    fetchRebookableClasses();
  }, [user, authLoading, navigate, fetchData, fetchRebookableClasses]);

  const handleCancelBooking = async (bookingId) => {
    try {
      await apiClient.post(`/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const getClassDetails = (classId) => {
    return classes.find(c => c.class_id === classId);
  };

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <Dumbbell className="w-8 h-8 text-primary" />
            <span className="font-heading text-2xl font-bold tracking-tight">T-Fit Inc Classes</span>
          </Link>
          <div className="flex gap-4 items-center">
            <Link to="/classes">
              <Button variant="ghost" data-testid="nav-classes-btn">Classes</Button>
            </Link>
            {user?.role === 'admin' && (
              <Link to="/admin">
                <Button variant="ghost" data-testid="nav-admin-btn">
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              </Link>
            )}
            <Link to="/profile">
              <Button variant="ghost" data-testid="nav-profile-btn">
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
            </Link>
            <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="font-heading text-5xl font-black mb-2" data-testid="dashboard-title">
              WELCOME BACK, <span className="text-primary">{user?.name?.toUpperCase()}</span>
            </h1>
            <p className="text-lg text-neutral-300">Manage your bookings and explore new classes</p>
          </motion.div>

          {/* Quick Book Again Section */}
          {rebookableClasses.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex items-center gap-2 mb-4">
                <RefreshCw className="w-5 h-5 text-primary" />
                <h2 className="font-heading text-xl font-bold">QUICK BOOK AGAIN</h2>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {rebookableClasses.slice(0, 3).map((item) => (
                  <div
                    key={item.title}
                    className="bg-background-paper border border-white/10 rounded-sm p-4"
                    data-testid={`rebook-card-${item.title}`}
                  >
                    <h3 className="font-bold mb-1">{item.title}</h3>
                    <p className="text-sm text-neutral-400 mb-2">{item.instructor} • {item.location}</p>
                    <p className="text-xs text-neutral-500 mb-3">£{item.price}</p>
                    
                    <div className="space-y-2">
                      <p className="text-xs uppercase tracking-wider text-neutral-500">Next available:</p>
                      {item.future_instances?.slice(0, 2).map((inst) => (
                        <Link
                          key={inst.class_id}
                          to={`/classes/${inst.class_id}`}
                          className={`block p-2 rounded-sm border text-sm ${
                            inst.already_booked 
                              ? 'border-success/30 bg-success/10 text-success' 
                              : 'border-white/10 hover:border-primary/50 hover:bg-primary/10'
                          }`}
                          data-testid={`rebook-instance-${inst.class_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              {format(new Date(inst.class_datetime), 'EEE, MMM d • h:mm a')}
                            </span>
                            {inst.already_booked ? (
                              <span className="text-xs">Booked</span>
                            ) : (
                              <span className="text-xs text-primary">Book →</span>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          <div className="grid lg:grid-cols-3 gap-8">
            {/* User Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-background-paper border border-white/10 rounded-sm p-6"
            >
              <div className="flex items-center gap-4 mb-6">
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center text-2xl font-bold">
                    {user?.name?.[0]?.toUpperCase()}
                  </div>
                )}
                <div>
                  <h2 className="font-heading text-xl font-bold" data-testid="user-name">{user?.name}</h2>
                  <p className="text-sm text-neutral-400" data-testid="user-email">{user?.email}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="bg-background border border-white/10 rounded-sm p-4">
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold" data-testid="total-bookings">{bookings.filter(b => b.status === 'active' || b.status === 'confirmed').length}</p>
                </div>
                <div className="bg-background border border-white/10 rounded-sm p-4">
                  <p className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Account Type</p>
                  <p className="text-lg font-bold capitalize" data-testid="user-role">{user?.role}</p>
                </div>
              </div>
            </motion.div>

            {/* Bookings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-3xl font-bold">YOUR BOOKINGS</h2>
                <Link to="/classes">
                  <Button data-testid="book-more-btn">BOOK MORE</Button>
                </Link>
              </div>

              {bookings.length === 0 ? (
                <div className="bg-background-paper border border-white/10 rounded-sm p-12 text-center" data-testid="no-bookings-message">
                  <Calendar className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                  <p className="text-lg text-neutral-400 mb-2">No bookings yet</p>
                  <p className="text-sm text-neutral-500 mb-6">Start your fitness journey by booking your first class!</p>
                  <Link to="/classes">
                    <Button>EXPLORE CLASSES</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {bookings.filter(b => b.status === 'active' || b.status === 'confirmed').map((booking, index) => {
                    const classDetails = getClassDetails(booking.class_id);
                    if (!classDetails) return null;

                    return (
                      <div
                        key={booking.booking_id}
                        className="bg-background-paper border border-white/10 rounded-sm p-6 hover:border-primary/50 transition-colors"
                        data-testid={`booking-card-${index}`}
                      >
                        <div className="flex gap-4">
                          <img
                            src={classDetails.image_url}
                            alt={classDetails.title}
                            className="w-24 h-24 object-cover rounded-sm"
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="font-heading text-xl font-bold mb-1">{classDetails.title}</h3>
                                <p className="text-sm text-neutral-400">{classDetails.instructor}</p>
                              </div>
                              <div className="flex flex-col items-end gap-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  booking.status === 'active' || booking.status === 'confirmed' ? 'bg-success/20 text-success' : 'bg-error/20 text-error'
                                }`}>
                                  {booking.status === 'confirmed' ? 'CONFIRMED' : booking.status.toUpperCase()}
                                </span>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                  booking.payment_status === 'completed' ? 'bg-info/20 text-info' : 'bg-warning/20 text-warning'
                                }`}>
                                  {booking.payment_status === 'completed' ? 'PAID' : booking.payment_status.toUpperCase()}
                                </span>
                                {booking.payment_status === 'completed' && (
                                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                    booking.payment_method === 'cash' || booking.payment_id === 'CASH_PAYMENT' 
                                      ? 'bg-emerald-500/20 text-emerald-400' 
                                      : 'bg-amber-500/20 text-amber-400'
                                  }`}>
                                    {booking.payment_method === 'cash' || booking.payment_id === 'CASH_PAYMENT' ? '💵 CASH' : '💳 PAYPAL'}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-6 text-sm text-neutral-400 mb-3">
                              <span>{format(new Date(classDetails.class_datetime), 'MMM dd, yyyy')}</span>
                              <span>{format(new Date(classDetails.class_datetime), 'h:mm a')}</span>
                              <span>{classDetails.duration} min</span>
                              <span className="font-bold">£{classDetails.price}</span>
                            </div>
                            {booking.status === 'active' && booking.payment_status !== 'completed' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="border-error text-error hover:bg-error hover:text-white"
                                onClick={() => handleCancelBooking(booking.booking_id)}
                                data-testid={`cancel-booking-btn-${index}`}
                              >
                                CANCEL BOOKING
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;